import { Section, CodeBlock, InfoBox } from "@/components"

export function ReactShadcnServicesPage() {
  return (
    <div className="space-y-10">
      <div className="space-y-4">
        <h1 className="text-4xl font-bold">API Services</h1>
        <p className="text-lg text-muted-foreground max-w-4xl leading-relaxed">
          Learn how API integration works in this template. The service layer handles all HTTP 
          requests with automatic token management, error handling, and TypeScript type safety.
        </p>
      </div>

      <Section title="Service Architecture" description="How services are organized">
        <div className="space-y-6">
          <p className="text-muted-foreground leading-relaxed">
            The template uses a centralized service layer built on Axios. Each service file 
            handles a specific domain (auth, users, etc.) and provides type-safe methods for API calls.
          </p>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-card border rounded-lg p-6 space-y-3">
              <h3 className="font-semibold">api.service.ts</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Base Axios instance with interceptors for authentication, error handling, and request/response transformation.
              </p>
            </div>
            <div className="bg-card border rounded-lg p-6 space-y-3">
              <h3 className="font-semibold">Domain Services</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Specific services (auth, user, dashboard) that use the base API instance to make type-safe requests.
              </p>
            </div>
          </div>
        </div>
      </Section>

      <Section title="Base API Service" description="Foundation for all API calls">
        <div className="space-y-6">
          <p className="text-muted-foreground leading-relaxed">
            The base API service configures Axios with interceptors for authentication and error handling.
          </p>
          
          <CodeBlock
            code={`import axios, { AxiosError } from "axios"
import type { ApiError } from "@/types/api.types"

// Create axios instance with base configuration
export const apiService = axios.create({
  baseURL: import.meta.env.VITE_API_URL || "http://localhost:3000/api",
  headers: {
    "Content-Type": "application/json"
  },
  timeout: 10000 // 10 seconds
})

// Request interceptor: Add auth token to requests
apiService.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token")
    if (token) {
      config.headers.Authorization = \`Bearer \${token}\`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor: Handle errors globally
apiService.interceptors.response.use(
  (response) => {
    return response
  },
  async (error: AxiosError<ApiError>) => {
    // Handle 401 Unauthorized
    if (error.response?.status === 401) {
      // Clear auth and redirect to login
      localStorage.removeItem("token")
      window.location.href = "/login"
    }

    // Format error message
    const message = error.response?.data?.message || error.message || "An error occurred"
    
    return Promise.reject({
      message,
      status: error.response?.status,
      data: error.response?.data
    })
  }
)

export default apiService`}
            language="typescript"
            filename="src/services/api.service.ts"
          />
          
          <InfoBox type="info" title="Automatic Token Management">
            The request interceptor automatically adds the JWT token to all requests. 
            If a 401 response is received, the user is automatically logged out.
          </InfoBox>
        </div>
      </Section>

      <Section title="Auth Service" description="Authentication endpoints">
        <div className="space-y-6">
          <p className="text-muted-foreground leading-relaxed">
            The auth service handles all authentication-related API calls with full type safety.
          </p>
          
          <CodeBlock
            code={`import { apiService } from "./api.service"
import type { User, LoginDto, RegisterDto, LoginResponse } from "@/types/auth.types"

export const authService = {
  /**
   * Login with email and password
   */
  async login(credentials: LoginDto): Promise<LoginResponse> {
    const response = await apiService.post<LoginResponse>(
      "/auth/login",
      credentials
    )
    return response.data
  },

  /**
   * Register a new user
   */
  async register(data: RegisterDto): Promise<LoginResponse> {
    const response = await apiService.post<LoginResponse>(
      "/auth/register",
      data
    )
    return response.data
  },

  /**
   * Get current authenticated user
   */
  async getCurrentUser(): Promise<User> {
    const response = await apiService.get<User>("/auth/me")
    return response.data
  },

  /**
   * Refresh access token
   */
  async refreshToken(refreshToken: string): Promise<{ token: string }> {
    const response = await apiService.post<{ token: string }>(
      "/auth/refresh",
      { refreshToken }
    )
    return response.data
  },

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    await apiService.post("/auth/logout")
  },

  /**
   * Request password reset
   */
  async forgotPassword(email: string): Promise<{ message: string }> {
    const response = await apiService.post<{ message: string }>(
      "/auth/forgot-password",
      { email }
    )
    return response.data
  },

  /**
   * Reset password with token
   */
  async resetPassword(token: string, password: string): Promise<{ message: string }> {
    const response = await apiService.post<{ message: string }>(
      "/auth/reset-password",
      { token, password }
    )
    return response.data
  }
}`}
            language="typescript"
            filename="src/services/auth.service.ts"
          />
        </div>
      </Section>

      <Section title="User Service" description="User management endpoints">
        <div className="space-y-6">
          <CodeBlock
            code={`import { apiService } from "./api.service"
import type { User, UpdateUserDto, PaginatedResponse } from "@/types/user.types"

export const userService = {
  /**
   * Get all users with pagination
   */
  async getUsers(page = 1, limit = 10): Promise<PaginatedResponse<User>> {
    const response = await apiService.get<PaginatedResponse<User>>(
      "/users",
      { params: { page, limit } }
    )
    return response.data
  },

  /**
   * Get user by ID
   */
  async getUser(id: string): Promise<User> {
    const response = await apiService.get<User>(\`/users/\${id}\`)
    return response.data
  },

  /**
   * Update user
   */
  async updateUser(id: string, data: UpdateUserDto): Promise<User> {
    const response = await apiService.patch<User>(\`/users/\${id}\`, data)
    return response.data
  },

  /**
   * Delete user
   */
  async deleteUser(id: string): Promise<void> {
    await apiService.delete(\`/users/\${id}\`)
  },

  /**
   * Search users
   */
  async searchUsers(query: string): Promise<User[]> {
    const response = await apiService.get<User[]>("/users/search", {
      params: { q: query }
    })
    return response.data
  }
}`}
            language="typescript"
            filename="src/services/user.service.ts"
          />
        </div>
      </Section>

      <Section title="Using Services in Components" description="Best practices">
        <div className="space-y-6">
          <p className="text-muted-foreground leading-relaxed">
            Services should be called from views or custom hooks, never directly from layout components.
          </p>
          
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Example: Login Form</h3>
            <CodeBlock
              code={`import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { authService } from "@/services/auth.service"
import { useAuthStore } from "@/store/auth.store"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { toast } from "@/components/ui/use-toast"

export function LoginView() {
  const navigate = useNavigate()
  const { setAuth } = useAuthStore()
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      // Call auth service
      const { user, token } = await authService.login(formData)
      
      // Update auth store
      setAuth(user, token)
      
      // Store token
      localStorage.setItem("token", token)
      
      // Navigate to dashboard
      navigate("/dashboard")
      
      toast({
        title: "Success",
        description: "Welcome back!"
      })
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to login",
        variant: "destructive"
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input
        type="email"
        placeholder="Email"
        value={formData.email}
        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
        required
      />
      <Input
        type="password"
        placeholder="Password"
        value={formData.password}
        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
        required
      />
      <Button type="submit" className="w-full" disabled={loading}>
        {loading ? "Logging in..." : "Login"}
      </Button>
    </form>
  )
}`}
              language="typescript"
            />
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Example: Custom Hook</h3>
            <CodeBlock
              code={`import { useState, useEffect } from "react"
import { userService } from "@/services/user.service"
import type { User } from "@/types/user.types"

export function useUsers(page = 1, limit = 10) {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [total, setTotal] = useState(0)

  const fetchUsers = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await userService.getUsers(page, limit)
      setUsers(response.data)
      setTotal(response.total)
    } catch (err: any) {
      setError(err.message || "Failed to fetch users")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [page, limit])

  return {
    users,
    loading,
    error,
    total,
    refetch: fetchUsers
  }
}

// Usage in component:
function UsersView() {
  const { users, loading, error, total, refetch } = useUsers(1, 10)

  if (loading) return <LoadingSpinner />
  if (error) return <ErrorMessage message={error} onRetry={refetch} />

  return <UsersList users={users} total={total} />
}`}
              language="typescript"
            />
          </div>
        </div>
      </Section>

      <Section title="Error Handling" description="Handling API errors gracefully">
        <div className="space-y-6">
          <p className="text-muted-foreground leading-relaxed">
            The template provides consistent error handling patterns for API calls.
          </p>
          
          <CodeBlock
            code={`import { useState } from "react"
import { userService } from "@/services/user.service"
import { toast } from "@/components/ui/use-toast"

function MyComponent() {
  const [loading, setLoading] = useState(false)

  const handleAction = async () => {
    setLoading(true)

    try {
      await userService.updateUser("123", { name: "New Name" })
      
      // Success notification
      toast({
        title: "Success",
        description: "User updated successfully"
      })
    } catch (error: any) {
      // Error notification
      toast({
        title: "Error",
        description: error.message || "Something went wrong",
        variant: "destructive"
      })

      // Log error for debugging
      console.error("Update failed:", error)
    } finally {
      setLoading(false)
    }
  }

  return <Button onClick={handleAction} disabled={loading}>Update</Button>
}`}
            language="typescript"
          />

          <InfoBox type="warning" title="Error Types">
            All service errors follow a consistent format with a <code className="bg-muted px-2 py-1 rounded">message</code>, 
            <code className="bg-muted px-2 py-1 rounded">status</code>, and optional <code className="bg-muted px-2 py-1 rounded">data</code> property.
          </InfoBox>
        </div>
      </Section>

      <Section title="TypeScript Types" description="Type-safe API calls">
        <div className="space-y-6">
          <p className="text-muted-foreground leading-relaxed">
            All services use TypeScript types to ensure type safety for requests and responses.
          </p>
          
          <CodeBlock
            code={`// types/auth.types.ts
export interface User {
  id: string
  email: string
  name: string
  role: "user" | "admin"
  createdAt: string
}

export interface LoginDto {
  email: string
  password: string
}

export interface RegisterDto {
  email: string
  password: string
  name: string
}

export interface LoginResponse {
  user: User
  token: string
  refreshToken: string
}

// types/api.types.ts
export interface ApiError {
  message: string
  errors?: Record<string, string[]>
}

export interface PaginatedResponse<T> {
  data: T[]
  total: number
  page: number
  limit: number
  totalPages: number
}`}
            language="typescript"
            filename="src/types/auth.types.ts"
          />
        </div>
      </Section>

      <div className="bg-primary/5 border border-primary/20 rounded-xl p-8 space-y-4">
        <h2 className="text-2xl font-bold">Service Best Practices</h2>
        <ul className="space-y-3 text-sm">
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Always define TypeScript types for request and response data</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Handle errors consistently with try/catch blocks</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Use custom hooks to encapsulate data fetching logic</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Show loading states while API calls are in progress</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Display user-friendly error messages with toast notifications</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Keep service methods focused and single-purpose</span>
          </li>
        </ul>
      </div>
    </div>
  )
}
